import { type JSX } from 'preact';
import { Card, CardContent, CardHeader, CardTitle } from '../../components';

export function SpecificationPage(): JSX.Element {
  return (
    <div className="max-w-4xl mx-auto">
      <Card>
        <CardHeader>
          <CardTitle>Shamir Secret Sharing - Technical Specification</CardTitle>
        </CardHeader>
        <CardContent className="prose prose-sm max-w-none">
          <div className="space-y-6">
            <section>
              <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">Overview</h2>
              <p className="text-gray-700 dark:text-gray-300">
                This document provides a complete specification of the Shamir Secret Sharing
                implementation used by this service. With this information, you can reconstruct your
                secrets using any programming language, even if this service becomes unavailable.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">Share Format</h2>
              <p className="text-gray-700 dark:text-gray-300">Each share follows this format:</p>
              <pre className="bg-gray-100 dark:bg-gray-800 p-3 rounded border border-gray-300 dark:border-gray-600 text-gray-900 dark:text-gray-100 overflow-x-auto">
                {`<index>-<base32-encoded-data>

Example: 1-JBSWY3DPFQQG64TM`}
              </pre>
              <ul className="list-disc list-inside text-gray-700 dark:text-gray-300 space-y-1">
                <li>
                  <strong>index</strong>: A number from 1-255 representing the share's x-coordinate
                  in GF(256)
                </li>
                <li>
                  <strong>base32-encoded-data</strong>: The share's y-coordinates encoded in base32
                  (RFC 4648)
                </li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">Base32 Encoding (RFC 4648)</h2>
              <p className="text-gray-700 dark:text-gray-300">
                Shares are encoded using base32 with the standard alphabet for human readability:
              </p>
              <pre className="bg-gray-100 dark:bg-gray-800 p-3 rounded border border-gray-300 dark:border-gray-600 text-gray-900 dark:text-gray-100">
                Alphabet: ABCDEFGHIJKLMNOPQRSTUVWXYZ234567
              </pre>
              <p className="text-gray-700 dark:text-gray-300">
                <strong>Decoding</strong>: Convert each base32 character to its 5-bit value, combine
                into bytes. Padding is not used in our implementation.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">Data Structure</h2>
              <p className="text-gray-700 dark:text-gray-300">
                After decoding the base32 data, the byte array contains:
              </p>
              <pre className="bg-gray-100 dark:bg-gray-800 p-3 rounded border border-gray-300 dark:border-gray-600 text-gray-900 dark:text-gray-100">
                {`[checksum (8 bytes)][secret data (N bytes)]

Checksum: First 8 bytes of SHA-256 hash of the secret
Secret:   UTF-8 encoded text`}
              </pre>
            </section>

            <section>
              <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">Galois Field GF(256) Arithmetic</h2>
              <p className="text-gray-700 dark:text-gray-300">
                All polynomial operations use arithmetic in GF(256) with the irreducible polynomial{' '}
                <code className="bg-gray-100 dark:bg-gray-800 px-1 rounded text-gray-900 dark:text-gray-100">x^8 + x^4 + x^3 + x + 1</code> (0x11d in
                hex).
              </p>

              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mt-4">Addition</h3>
              <pre className="bg-gray-100 dark:bg-gray-800 p-3 rounded border border-gray-300 dark:border-gray-600 text-gray-900 dark:text-gray-100">
                {`a ⊕ b = a XOR b`}
              </pre>

              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mt-4">
                Multiplication (using log/exp tables)
              </h3>
              <pre className="bg-gray-100 dark:bg-gray-800 p-3 rounded border border-gray-300 dark:border-gray-600 text-gray-900 dark:text-gray-100 overflow-x-auto">
                {`// Generate tables once:
EXP[0..255] and LOG[0..255]

x = 1
for i = 0 to 254:
    EXP[i] = x
    LOG[x] = i
    x = (x << 1) XOR (0x11d if x & 0x80 else 0)
EXP[255] = EXP[0]

// Multiplication:
multiply(a, b):
    if a == 0 or b == 0: return 0
    return EXP[(LOG[a] + LOG[b]) mod 255]

// Division:
divide(a, b):
    if b == 0: error "division by zero"
    if a == 0: return 0
    return EXP[(LOG[a] - LOG[b] + 255) mod 255]`}
              </pre>
            </section>

            <section>
              <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">Secret Reconstruction</h2>

              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mt-4">
                Step 1: Parse and Decode Shares
              </h3>
              <pre className="bg-gray-100 dark:bg-gray-800 p-3 rounded border border-gray-300 dark:border-gray-600 text-gray-900 dark:text-gray-100 overflow-x-auto">
                {`For each share string:
  1. Split on '-' to get index and encoded data
  2. Parse index as integer (1-255)
  3. Decode base32 data to byte array
  4. Verify all shares have same length`}
              </pre>

              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mt-4">
                Step 2: Lagrange Interpolation at x=0
              </h3>
              <p className="text-gray-700 dark:text-gray-300">
                For each byte position in the shares, reconstruct the original byte using Lagrange
                interpolation in GF(256):
              </p>
              <pre className="bg-gray-100 dark:bg-gray-800 p-3 rounded border border-gray-300 dark:border-gray-600 text-gray-900 dark:text-gray-100 overflow-x-auto">
                {`function interpolate(shares):
    // shares = array of {x: index, y: byte_value}
    result = 0

    for i = 0 to shares.length - 1:
        numerator = 1
        denominator = 1

        for j = 0 to shares.length - 1:
            if i == j: continue

            // Evaluate at x=0, so numerator is just shares[j].x
            numerator = multiply(numerator, shares[j].x)

            // Denominator is (shares[i].x - shares[j].x)
            denominator = multiply(denominator,
                                 shares[i].x XOR shares[j].x)

        // Lagrange basis polynomial evaluated at 0
        basis = divide(numerator, denominator)

        // Add contribution of this share
        term = multiply(shares[i].y, basis)
        result = result XOR term

    return result

// Reconstruct all bytes:
reconstructed = empty byte array
for byte_position = 0 to share_length - 1:
    points = []
    for each share:
        points.append({x: share.index, y: share.data[byte_position]})

    reconstructed[byte_position] = interpolate(points)`}
              </pre>

              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mt-4">Step 3: Verify Checksum</h3>
              <pre className="bg-gray-100 dark:bg-gray-800 p-3 rounded border border-gray-300 dark:border-gray-600 text-gray-900 dark:text-gray-100 overflow-x-auto">
                {`stored_checksum = reconstructed[0:8]
secret_bytes = reconstructed[8:]

computed_checksum = SHA256(secret_bytes)[0:8]

if stored_checksum != computed_checksum:
    error "Checksum verification failed"
    // Wrong indices, insufficient shares, or corrupted data`}
              </pre>

              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mt-4">Step 4: Decode Secret</h3>
              <pre className="bg-gray-100 dark:bg-gray-800 p-3 rounded border border-gray-300 dark:border-gray-600 text-gray-900 dark:text-gray-100">
                {`secret = decode_utf8(secret_bytes)`}
              </pre>
            </section>

            <section>
              <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">Complete Example</h2>
              <p className="text-gray-700 dark:text-gray-300">
                Given shares for the secret "Hello":
              </p>
              <pre className="bg-gray-100 dark:bg-gray-800 p-3 rounded border border-gray-300 dark:border-gray-600 text-gray-900 dark:text-gray-100 overflow-x-auto text-xs">
                {`1-KBQXA5DJNNSXGX3QMFZXGIDUNBSSA===
2-KBQWY3DPNZVWCX3BMVZXG5LUNFSSA===
3-KBQW4YLQNRXWO4TBNFXW4IDRPBSSA===

Reconstruction process:
1. Parse: {x:1, data:[...]}, {x:2, data:[...]}, {x:3, data:[...]}
2. Decode base32 to bytes
3. For each byte position, interpolate:
   - GF(256) Lagrange interpolation
   - Reconstruct original polynomial value at x=0
4. Result: [checksum (8 bytes)][0x48, 0x65, 0x6c, 0x6c, 0x6f]
5. Verify checksum matches SHA256("Hello")[0:8]
6. Decode UTF-8: "Hello"`}
              </pre>
            </section>

            <section>
              <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">Reference Implementation</h2>
              <p className="text-gray-700 dark:text-gray-300">Python pseudocode for complete reconstruction:</p>
              <pre className="bg-gray-100 dark:bg-gray-800 p-3 rounded border border-gray-300 dark:border-gray-600 text-gray-900 dark:text-gray-100 overflow-x-auto text-xs">
                {`import hashlib
import base64

# GF(256) tables
EXP = [0] * 256
LOG = [0] * 256

def init_gf256():
    x = 1
    for i in range(255):
        EXP[i] = x
        LOG[x] = i
        x = (x << 1) ^ (0x11d if x & 0x80 else 0)
    EXP[255] = EXP[0]

def gf_multiply(a, b):
    if a == 0 or b == 0: return 0
    return EXP[(LOG[a] + LOG[b]) % 255]

def gf_divide(a, b):
    if b == 0: raise ValueError("Division by zero")
    if a == 0: return 0
    return EXP[(LOG[a] - LOG[b] + 255) % 255]

def decode_base32(s):
    alphabet = "ABCDEFGHIJKLMNOPQRSTUVWXYZ234567"
    result = []
    buffer, bits = 0, 0
    for c in s.upper():
        if c not in alphabet: continue
        buffer = (buffer << 5) | alphabet.index(c)
        bits += 5
        if bits >= 8:
            result.append((buffer >> (bits - 8)) & 255)
            bits -= 8
    return bytes(result)

def interpolate(shares):
    result = 0
    for i, (xi, yi) in enumerate(shares):
        numerator = 1
        denominator = 1
        for j, (xj, yj) in enumerate(shares):
            if i == j: continue
            numerator = gf_multiply(numerator, xj)
            denominator = gf_multiply(denominator, xi ^ xj)
        basis = gf_divide(numerator, denominator)
        term = gf_multiply(yi, basis)
        result ^= term
    return result

def reconstruct_secret(share_strings):
    init_gf256()

    # Parse shares
    shares = []
    for s in share_strings:
        idx, data = s.split('-', 1)
        shares.append((int(idx), decode_base32(data)))

    # Check lengths
    length = len(shares[0][1])
    if not all(len(data) == length for _, data in shares):
        raise ValueError("Share length mismatch")

    # Reconstruct each byte
    result = bytearray(length)
    for pos in range(length):
        points = [(idx, data[pos]) for idx, data in shares]
        result[pos] = interpolate(points)

    # Verify checksum
    checksum = bytes(result[:8])
    secret_bytes = bytes(result[8:])
    expected = hashlib.sha256(secret_bytes).digest()[:8]

    if checksum != expected:
        raise ValueError("Checksum verification failed")

    return secret_bytes.decode('utf-8')

# Usage:
shares = [
    "1-KBQXA5DJNNSXGX3QMFZXGIDUNBSSA",
    "2-KBQWY3DPNZVWCX3BMVZXG5LUNFSSA",
    "3-KBQW4YLQNRXWO4TBNFXW4IDRPBSSA"
]
secret = reconstruct_secret(shares)
print(secret)  # "Hello"`}
              </pre>
            </section>

            <section>
              <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">Important Notes</h2>
              <ul className="list-disc list-inside text-gray-700 dark:text-gray-300 space-y-2">
                <li>
                  <strong>Share indices matter</strong>: The index in each share (before the dash)
                  is critical for reconstruction. Changing it will result in garbage output or
                  checksum failure.
                </li>
                <li>
                  <strong>Threshold requirement</strong>: You need exactly k shares (the threshold)
                  or more. Fewer shares will fail checksum verification.
                </li>
                <li>
                  <strong>Case insensitive</strong>: Base32 decoding is case-insensitive. Spaces are
                  ignored.
                </li>
                <li>
                  <strong>No padding</strong>: Our base32 encoding omits padding characters (=).
                </li>
                <li>
                  <strong>UTF-8 encoding</strong>: Secrets are encoded as UTF-8 text. Binary data is
                  not supported.
                </li>
                <li>
                  <strong>Maximum shares</strong>: Index values range from 1-255 (GF(256)
                  constraint).
                </li>
              </ul>
            </section>

            <section className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded border border-blue-200 dark:border-blue-800">
              <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">Save This Page</h2>
              <p className="text-gray-700 dark:text-gray-300">
                We recommend saving this specification page (File → Save Page As) along with your
                shares. This ensures you can reconstruct your secrets even if this service becomes
                unavailable in the future.
              </p>
            </section>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
